import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserPlus, UserCheck, Search, Loader2, BadgeCheck, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { fetchSuggestedUsers, SuggestedUser } from '../services/suggestionsService';
import { FollowService } from '../services/followService';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useScrollLock } from '../hooks/useScrollLock';
import { useBackButton } from '../hooks/useBackButton';
import '../styles/modal.css';

export interface SuggestedUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SuggestedUsersModal: React.FC<SuggestedUsersModalProps> = ({ isOpen, onClose }) => {
  const { user: authUser } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [mounted, setMounted] = useState(false);
  const [users, setUsers] = useState<SuggestedUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<SuggestedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [following, setFollowing] = useState<Record<string, boolean>>({});
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [followerCounts, setFollowerCounts] = useState<Record<string, number>>({});
  const scrollRef = useRef<HTMLDivElement>(null);

  // Safe client-side mount check
  useEffect(() => {
    setMounted(true);
  }, []);

  // Load suggested users when modal opens
  useEffect(() => {
    if (!isOpen) return;

    let active = true;
    const loadUsers = async () => {
      try {
        setLoading(true);
        const suggested = await fetchSuggestedUsers(50, authUser?.uid ?? undefined);
        if (active) {
          setUsers(suggested);
          setFilteredUsers(suggested);
        }
      } catch (error) {
        console.error('Error loading suggested users:', error);
        showToast('Failed to load suggested users', 'error');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadUsers();
    return () => {
      active = false;
    };
  }, [isOpen, authUser?.uid, showToast]);

  // Filter users based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = users.filter(user =>
      user.username.toLowerCase().includes(query) ||
      user.displayName.toLowerCase().includes(query) ||
      (user.specialties && user.specialties.some(s => s.toLowerCase().includes(query)))
    );
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  // Fetch follow states and follower counts once
  useEffect(() => {
    if (!users.length || !authUser?.uid) return;

    const fetchStates = async () => {
      const followStates: Record<string, boolean> = {};
      const counts: Record<string, number> = {};

      await Promise.all(
        users.map(async (user) => {
          try {
            const isFollowing = await FollowService.checkIsFollowing(authUser.uid, user.id);
            followStates[user.id] = isFollowing;

            const userDoc = await getDoc(doc(db, 'users', user.id));
            if (userDoc.exists()) {
              const data = userDoc.data();
              counts[user.id] = data?.followersCount || data?.followers?.length || 0;
            }
          } catch {
            // Silently handle errors for individual users
          }
        })
      );

      setFollowing(followStates);
      setFollowerCounts(counts);
    };

    fetchStates();
  }, [users, authUser?.uid]);

  const handleFollow = async (user: SuggestedUser) => {
    if (!authUser?.uid) {
      showToast('Please sign in to follow users', 'error');
      return;
    }
    if (pending[user.id]) return;

    const wasFollowing = !!following[user.id];
    const nowFollowing = !wasFollowing;

    setPending(prev => ({ ...prev, [user.id]: true }));
    setFollowing(prev => ({ ...prev, [user.id]: nowFollowing }));
    setFollowerCounts(prev => ({
      ...prev,
      [user.id]: Math.max(0, (prev[user.id] ?? 0) + (nowFollowing ? 1 : -1))
    }));

    try {
      const res = await FollowService.toggleFollow(authUser.uid, user.id);
      setFollowing(prev => ({ ...prev, [user.id]: res.following }));
      setFollowerCounts(prev => ({
        ...prev,
        [user.id]: Math.max(0, (prev[user.id] ?? 0) + (res.following === nowFollowing ? 0 : (res.following ? 1 : -1)))
      }));
      showToast(res.following ? `Following ${user.displayName}` : `Unfollowed ${user.displayName}`, res.following ? 'success' : 'info');
    } catch (e) {
      setFollowing(prev => ({ ...prev, [user.id]: wasFollowing }));
      setFollowerCounts(prev => ({
        ...prev,
        [user.id]: Math.max(0, (prev[user.id] ?? 0) + (wasFollowing ? 1 : -1))
      }));
      showToast('Failed to update follow. Please try again.', 'error');
    } finally {
      setPending(prev => ({ ...prev, [user.id]: false }));
    }
  };

  const handleUserClick = useCallback((user: SuggestedUser) => {
    onClose();
    navigate(`/profile/${user.username}`);
  }, [navigate, onClose]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getSubtitle = (user: SuggestedUser) => {
    if (user.specialties?.length) return user.specialties[0];
    const count = followerCounts[user.id] ?? 0;
    if (count > 0) return `${count.toLocaleString()} followers`;
    if (user.verified) return 'Verified account';
    return 'Suggested for you';
  };

  // Support mobile hardware back button
  useBackButton(isOpen, onClose);

  // Lock background body scroll when modal is open
  useScrollLock({
    isLocked: isOpen,
    preserveScrollbarWidth: true,
  });

  // Support Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!mounted || typeof document === 'undefined') return null;

  // Retrieve or dynamically create modal-root portal target in DOM
  const getModalRoot = (): HTMLElement => {
    let el = document.getElementById('modal-root');
    if (!el) {
      el = document.createElement('div');
      el.id = 'modal-root';
      document.body.appendChild(el);
    }
    return el;
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="suggested-users-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="modal-backdrop-overlay fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6 bg-black/75 backdrop-blur-md"
          onClick={onClose}
          aria-modal="true"
          role="dialog"
          aria-labelledby="suggested-users-title"
        >
          <motion.div
            key="suggested-users-dialog"
            initial={{ scale: 0.95, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 16 }}
            transition={{ type: 'spring', damping: 26, stiffness: 320 }}
            className="modal-dialog-container relative w-full max-w-md max-h-[85vh] sm:max-h-[80vh] flex flex-col bg-white dark:bg-[#0F121A] text-neutral-900 dark:text-white border border-neutral-200 dark:border-white/10 rounded-2xl shadow-2xl overflow-hidden isolate"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-white/10 bg-white/80 dark:bg-[#0F121A]/80 backdrop-blur-md">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 dark:text-violet-400">
                  <Users className="w-4 h-4" />
                </div>
                <div>
                  <h2 id="suggested-users-title" className="text-base font-semibold text-neutral-900 dark:text-white leading-tight">
                    Suggested for you
                  </h2>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    Discover creators and expand your network
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-full text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search */}
            <div className="flex-shrink-0 px-4 py-3 border-b border-neutral-200 dark:border-white/10 bg-neutral-50/50 dark:bg-white/[0.01]">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 dark:text-neutral-500" />
                <input
                  type="text"
                  placeholder="Search creators by name, handle, or specialty..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-xl bg-white dark:bg-white/5 border border-neutral-200 dark:border-white/10 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm transition-all shadow-sm"
                  autoFocus={false}
                />
              </div>
            </div>

            {/* Independent Scroll Container for User List */}
            <div 
              ref={scrollRef}
              className="modal-scroll-container flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 py-2"
              tabIndex={0}
            >
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 animate-pulse">
                    Finding suggestions...
                  </p>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <div className="w-12 h-12 rounded-full bg-neutral-100 dark:bg-white/5 flex items-center justify-center mx-auto mb-3 text-neutral-400">
                    <Users className="w-6 h-6" />
                  </div>
                  <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {searchQuery ? 'No creators found' : 'No suggestions available'}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">
                    {searchQuery ? 'Try searching with a different term' : 'Check back later for new creators'}
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredUsers.map(user => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 rounded-xl hover:bg-neutral-100/70 dark:hover:bg-white/[0.04] transition-colors group"
                    >
                      <div 
                        className="flex items-center space-x-3 flex-1 min-w-0 cursor-pointer pr-3"
                        onClick={() => handleUserClick(user)}
                      >
                        {user.avatar ? (
                          <img
                            src={user.avatar}
                            alt={user.displayName}
                            className="w-11 h-11 rounded-full object-cover ring-2 ring-transparent group-hover:ring-violet-500/30 transition-all flex-shrink-0"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 shadow-sm">
                            {getInitials(user.displayName)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-semibold text-neutral-900 dark:text-white truncate group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
                              {user.displayName}
                            </p>
                            {user.verified && (
                              <BadgeCheck className="w-4 h-4 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                            @{user.username}
                          </p>
                          <p className="text-[11px] text-neutral-400 dark:text-neutral-500 truncate mt-0.5">
                            {getSubtitle(user)}
                          </p>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => handleFollow(user)}
                        disabled={pending[user.id]}
                        className={`flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 flex-shrink-0 ${
                          following[user.id]
                            ? 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700 border border-neutral-300 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/30 dark:hover:bg-green-500/20'
                            : 'bg-violet-600 hover:bg-violet-700 text-white shadow-sm hover:shadow active:scale-95'
                        } ${pending[user.id] ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        {pending[user.id] ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : following[user.id] ? (
                          <>
                            <UserCheck className="w-3.5 h-3.5" />
                            <span>Following</span>
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-3.5 h-3.5" />
                            <span>Follow</span>
                          </>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-5 py-3 border-t border-neutral-200 dark:border-white/10 bg-neutral-50/80 dark:bg-white/[0.02]">
              <p className="text-[11px] text-center text-neutral-500 dark:text-neutral-400">
                Creators recommended based on your activity, mutual connections, and interests
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    getModalRoot()
  );
};

export default SuggestedUsersModal;
